import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";

// Re-export constants for easier access
export const TOKEN_COSTS = {
  AI_PROMPT_GENERATION: 5,
  FORM_AUTO_FILL: 3,
  URL_METADATA: 1,
} as const;

export const INITIAL_TOKEN_ALLOCATION = 100;
const LOW_BALANCE_THRESHOLD = 20;

export function useTokenBalance() {
  const balanceQuery = useQuery(api.tokens.getUserBalance);
  const initializeTokens = useMutation(api.tokens.initializeUserTokens);
  
  // Auto-initialize tokens if needed
  useEffect(() => {
    if (balanceQuery?.needsInitialization) {
      void initializeTokens();
    }
  }, [balanceQuery?.needsInitialization, initializeTokens]);

  return {
    balance: balanceQuery?.balance ?? 0,
    totalAllocated: balanceQuery?.totalAllocated ?? 0,
    totalConsumed: balanceQuery?.totalConsumed ?? 0,
    isLow: balanceQuery?.isLow ?? false,
    lastUpdated: balanceQuery?.lastUpdated ?? Date.now(),
    isLoading: balanceQuery === undefined,
    needsInitialization: balanceQuery?.needsInitialization ?? false,
  };
}

export function useTokenTransactions(limit: number = 20) {
  const transactions = useQuery(api.tokens.getUserTransactions, { limit });
  return transactions;
}

function useTokenAvailability(amount: number) {
  return useQuery(api.tokens.checkTokenAvailability, { amount });
}

function useConsumeTokens() {
  const consumeTokens = useMutation(api.tokens.consumeTokens);
  
  return useCallback(async (args: {
    amount: number;
    reason: string;
    apiDetails?: {
      service: string;
      endpoint: string;
      tokensUsed: number;
      estimatedCost: number;
    };
  }) => {
    try {
      const result = await consumeTokens(args);
      
      if (result.isLow) {
        toast.warning("Token balance is running low", {
          description: `You have ${result.newBalance} tokens remaining. Consider managing your usage.`,
        });
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to consume tokens';
      
      if (errorMessage.includes('Insufficient tokens')) {
        toast.error("Insufficient tokens", {
          description: "You don't have enough tokens for this operation. Your usage has been limited to protect against excessive costs.",
        });
      } else if (errorMessage.includes('Rate limit')) {
        toast.error("Rate limit exceeded", {
          description: "You're making requests too quickly. Please wait a moment and try again.",
        });
      } else {
        toast.error("Token consumption failed", {
          description: errorMessage,
        });
      }
      
      throw error;
    }
  }, [consumeTokens]);
}

/**
 * Hook for protected API operations that require token consumption
 * This provides pre-flight token checking and consistent error handling
 */
function useTokenGuard() {
  const { balance } = useTokenBalance();
  
  return useCallback(async (operation: 'ai_prompt' | 'form_auto_fill' | 'url_metadata') => {
    const tokenCost = {
      'ai_prompt': TOKEN_COSTS.AI_PROMPT_GENERATION,
      'form_auto_fill': TOKEN_COSTS.FORM_AUTO_FILL,
      'url_metadata': TOKEN_COSTS.URL_METADATA,
    }[operation];

    // Check if user has enough tokens
    if (balance < tokenCost) {
      const error = new Error(`Insufficient tokens. You have ${balance} tokens but need ${tokenCost} for this operation.`);
      toast.error("Insufficient tokens", {
        description: error.message,
      });
      throw error;
    }
    
    if ((balance - tokenCost) < LOW_BALANCE_THRESHOLD) {
      toast.warning("Token balance will be low", {
        description: `After this operation, you'll have ${balance - tokenCost} tokens remaining.`,
      });
    }
    
    return tokenCost;
  }, [balance]);
}

export function formatTokenReason(reason: string): string {
  const reasonMap: Record<string, string> = {
    'initial_allocation': 'Initial Token Allocation',
    'ai_prompt_generation': 'AI Prompt Generation',
    'form_auto_fill': 'AI Form Auto-Fill',
    'url_metadata': 'URL Metadata Extraction',
    'admin_grant': 'Admin Token Grant',
  };
  
  return reasonMap[reason] || reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export function useAddTokensToUser() {
  return useMutation(api.tokens.addTokensToUser);
}

export function useSystemStats() {
  return useQuery(api.tokens.getSystemStats);
}

export function useTokenAnalytics(days: number = 7) {
  return useQuery(api.tokens.getTokenAnalytics, { days });
} 